import { Table, Th, Td, fmtUsd } from "@/components/data-table";
import { ScrambleText } from "@/components/scramble-text";
import { ChainBadge } from "@/components/chain-badge";
import { CardImage } from "@/components/card-image";
import { useWatchlist } from "@/hooks/use-watchlist";

const SLAB_NFT_CONTRACT = "0x8a981c2cfdd7fbc65395dd2c02ead94e9a2f65a7";

function externalLink(entry: {
  kind: "CARD" | "PACK";
  chain: "base" | "apechain";
  address: string;
  tokenId: string;
}): string {
  if (entry.kind === "CARD") {
    // OpenSea supports both Base and ApeChain (the ApeChain slug is
    // `ape_chain`, with an underscore).
    const slug = entry.chain === "base" ? "base" : "ape_chain";
    return `https://opensea.io/assets/${slug}/${SLAB_NFT_CONTRACT}/${entry.tokenId}`;
  }
  // Pack — link to slab.cash machine page if we can guess the slug,
  // otherwise generic explorer.
  if (entry.chain === "base") {
    return `https://basescan.org/address/${entry.address}`;
  }
  return `https://apescan.io/address/${entry.address}`;
}

function short(a: string) {
  if (!a) return "";
  return a.slice(0, 6) + "…" + a.slice(-4);
}

export default function WatchlistPage() {
  const { entries, remove } = useWatchlist();

  // Newest-first so recent adds bubble up.
  const items = [...entries].sort((a, b) => b.addedAt - a.addedAt);

  return (
    <div className="p-3 md:p-4 space-y-3 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div className="space-y-1">
          <h1 className="text-xl font-bold uppercase tracking-[0.2em] neon-text cursor">
            <ScrambleText text="watchlist" duration={900} revealEach={45} />
          </h1>
          <p className="text-[11px] text-muted max-w-3xl font-mono">
            <span className="text-accent2">›</span> saved locally in your browser — no sign-in
            required. clears if you wipe site storage.
          </p>
        </div>
        <div className="text-[11px] font-mono text-muted">
          <span className="text-accent2">›</span> {items.length} saved
        </div>
      </div>

      {items.length === 0 ? (
        <div className="rounded border border-border bg-panel/60 p-6 text-sm text-muted font-mono">
          <span className="text-accent2">›</span> your list is empty. click &quot;watch&quot; on any
          card or pack to add it here.
        </div>
      ) : (
        <>
        {/* Mobile: stacked rows, image + meta + remove button. */}
        <div className="md:hidden space-y-2">
          {items.map((it) => {
            const href = externalLink(it);
            return (
              <div
                key={it.key}
                className="flex items-start gap-3 rounded-md border border-border bg-panel/70 p-2 animate-fadeIn"
              >
                <div className="h-[80px] w-[60px] shrink-0 overflow-hidden rounded border border-border bg-bg">
                  {it.imageUrl ? (
                    <CardImage
                      src={it.imageUrl}
                      alt={it.description ?? it.tokenId}
                      size={180}
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-[9px] text-muted font-mono">
                      {it.kind === "PACK" ? "PACK" : "?"}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0 text-[11px] font-mono">
                  <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-text hover:text-accent transition-colors block truncate"
                  >
                    {it.description ?? short(it.address)}
                  </a>
                  <div className="flex items-center gap-2 mt-0.5 text-[10px]">
                    <span className="text-accent2 uppercase tracking-wider">{it.kind}</span>
                    <ChainBadge chain={it.chain} />
                  </div>
                  {it.rarity && (
                    <div className="text-[10px] text-muted mt-0.5">{it.rarity}</div>
                  )}
                  <div className="flex items-center justify-between mt-1 pt-1 border-t border-border/60">
                    <span className="text-muted tabular-nums">
                      {fmtUsd(it.valueUsd)}
                    </span>
                    <button
                      onClick={() => remove(it.key)}
                      className="rounded border border-border px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-danger hover:border-danger hover:bg-danger/10 transition-all"
                    >
                      [remove]
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <Table>
          <thead>
            <tr>
              <Th />
              <Th>Item</Th>
              <Th>Kind</Th>
              <Th>Chain</Th>
              <Th>Token</Th>
              <Th className="text-right">Value</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const href = externalLink(it);
              return (
                <tr key={it.key} className="hover:bg-border/20 row-glow">
                  <Td className="w-[88px]">
                    <div className="h-[80px] w-[60px] overflow-hidden rounded border border-border bg-bg">
                      {it.imageUrl ? (
                        <CardImage
                          src={it.imageUrl}
                          alt={it.description ?? it.tokenId}
                          size={180}
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-[9px] text-muted font-mono">
                          {it.kind === "PACK" ? "PACK" : "?"}
                        </div>
                      )}
                    </div>
                  </Td>
                  <Td>
                    <a
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium text-text hover:text-accent transition-colors"
                    >
                      {it.description ?? short(it.address)}
                    </a>
                    {it.rarity && (
                      <div className="text-[10px] text-muted font-mono">{it.rarity}</div>
                    )}
                  </Td>
                  <Td>
                    <span className="text-[10px] font-mono uppercase tracking-wider text-accent2">
                      {it.kind}
                    </span>
                  </Td>
                  <Td>
                    <ChainBadge chain={it.chain} />
                  </Td>
                  <Td className="font-mono text-xs">{it.tokenId || short(it.address)}</Td>
                  <Td className="text-right">{fmtUsd(it.valueUsd)}</Td>
                  <Td>
                    <button
                      onClick={() => remove(it.key)}
                      className="rounded border border-border px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-danger hover:border-danger hover:bg-danger/10 transition-all"
                    >
                      [remove]
                    </button>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        </>
      )}
    </div>
  );
}
